import type { TimesheetListItem } from '@tabel/shared/schemas/timesheets';
import { CalendarDays, Check, Download, MessageCircleQuestion, MoreHorizontal, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { downloadExport } from '../lib/api';
import { ruDate, shownHours } from '../lib/format';
import { useToast } from './ErrorToast';
import { Badge, Dropdown, IconButton, MenuItem, MenuSeparator, StatusBadge } from './ui';

/**
 * Сетка карточек табелей (дизайн-система, Card и StatStrip): заголовок, статус, период, сводка в приглушённой зоне.
 * На телефоне — одна колонка. В углу карточки рядом со статусом — меню «…» действий с табелем: «Скачать Excel»,
 * у черновика с `onDelete` — последним красным пунктом «Удалить черновик» (дизайн-система, Dropdown).
 * Кнопка меню лежит вне ссылки карточки: интерактивный элемент внутри ссылки недопустим.
 */
export function TimesheetCards({
  items,
  onDelete,
}: { items: TimesheetListItem[]; onDelete?: (t: TimesheetListItem) => void }) {
  const { showError } = useToast();
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(300px,100%),1fr))] gap-4">
      {items.map((t) => (
        <div key={t.id} className="group relative flex">
          <Link
            to={`/timesheets/${t.id}`}
            className="flex flex-1 flex-col overflow-hidden rounded-card border border-line bg-surface shadow-card transition-colors hover:border-line-strong"
          >
            <div className="px-5 pt-4 pb-3">
              {/* Место справа — под кнопку «…»; период ниже — на всю ширину карточки, без переноса. */}
              <div className="flex items-center justify-between gap-3 pr-7 max-sm:pr-8">
                <p className="min-w-0 truncate text-[15px] font-semibold text-ink group-hover:text-accent">
                  {t.brigadeName}
                </p>
                <StatusBadge status={t.status} />
              </div>
              <p className="mt-1 flex items-center gap-1.5 text-[13px] text-ink-2 tabular-nums">
                <CalendarDays size={14} strokeWidth={1.75} className="text-ink-3" aria-hidden />
                {ruDate(t.periodStart)} — {ruDate(t.periodEnd)}
              </p>
              {/* Вопросы бригадиру (003, FR-022): без функции вопросов поля нет — и пометки нет. */}
              {t.openQuestions !== undefined && (
                <p className="mt-2">
                  {t.openQuestions > 0 ? (
                    <Badge tone="warn">
                      <MessageCircleQuestion size={12} strokeWidth={2} aria-hidden />
                      {t.openQuestions} без ответа
                    </Badge>
                  ) : (
                    <Badge tone="ok">
                      <Check size={12} strokeWidth={2} aria-hidden />
                      все отвечены
                    </Badge>
                  )}
                </p>
              )}
            </div>
            <dl className="mt-auto grid grid-cols-2 border-t border-line bg-subtle">
              <div className="px-5 py-2.5">
                <dt className="text-[12px] text-ink-3">Людей</dt>
                <dd className="text-[15px] font-semibold text-ink tabular-nums">{t.peopleCount ?? '—'}</dd>
              </div>
              <div className="border-l border-line px-5 py-2.5">
                <dt className="text-[12px] text-ink-3">Часов</dt>
                <dd className="text-[15px] font-semibold text-ink tabular-nums">
                  {t.totalHours == null ? '—' : shownHours(t.totalValue, t.totalHours)}
                </dd>
              </div>
            </dl>
          </Link>
          <div className="absolute top-2.5 right-3 max-sm:top-2 max-sm:right-2">
            <Dropdown
              align="end"
              width="w-56"
              trigger={({ open, toggle }) => (
                <IconButton
                  icon={MoreHorizontal}
                  label={`Действия с табелем «${t.brigadeName}»`}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={toggle}
                />
              )}
            >
              {(close) => (
                <>
                  <MenuItem
                    icon={Download}
                    onClick={() => {
                      close();
                      downloadExport(t.id).catch((e) =>
                        showError(e, { screen: 'TimesheetCards', action: 'export' }),
                      );
                    }}
                  >
                    Скачать Excel
                  </MenuItem>
                  {onDelete && t.status === 'draft' && (
                    <>
                      <MenuSeparator />
                      <MenuItem
                        icon={Trash2}
                        danger
                        onClick={() => {
                          close();
                          onDelete(t);
                        }}
                      >
                        Удалить черновик
                      </MenuItem>
                    </>
                  )}
                </>
              )}
            </Dropdown>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Секция страницы: подзаголовок с количеством и содержимое. */
export function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-[14px] font-semibold text-ink">
        {title}
        <span className="rounded-full bg-subtle px-2 py-0.5 text-[12px] font-medium text-ink-2 ring-1 ring-line ring-inset tabular-nums">
          {count}
        </span>
      </h2>
      {children}
    </section>
  );
}
